/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { Else, If, Then } from "react-if";
import styled from "styled-components";
import {
  Button,
  Description,
  Dropdown,
  ExtendedPaginationBox,
  FlexContainer,
  Heading,
  Input,
  Label,
  Sidebar,
  Switch,
  Tag,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  Role,
  useApplicationsPaginatedListQuery,
  UserPermissions,
  useSetUserPermissionsMutation,
} from "~/generated/graphql";
import { Fieldset, LoaderWrap } from "~/components";
import { useDisclosure } from "~/hooks";
import { PaginationProps } from "~/models";
import { handleError, isNotNil } from "~/tools";
import { showRole } from "~/views/settings/UsersAndPermissions/showRole";
import { UsersAndPermissionsSchema } from "~/views/settings/validators/usersAndPermissions";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  refetch: () => void;
  edit?: boolean;
  editItem?: UserPermissions;
};

// TODO: Reduce amount of code
// eslint-disable-next-line max-lines-per-function
export function AddUserPermissionsSidebar({
  isOpen,
  onClose,
  refetch,
  edit,
  editItem,
}: Props) {
  const [appState, setAppState] = useState<PaginationProps>({
    page: 0,
    limit: 20,
  });
  const {
    isOpen: isAppSidebarOpen,
    close: closeAppSidebar,
    open: openAppSidebar,
  } = useDisclosure(false);
  const [userRole, setUserRole] = useState<Role>(Role.Admin);
  const [selectedApplications, setSelectedApplications] = useState<string[]>(
    []
  );

  const {
    register,
    handleSubmit,
    setValue,
    reset,

    formState: { errors },
  } = useForm({
    resolver: yupResolver(UsersAndPermissionsSchema),
  });

  const [setUserPermissions, { loading: isSetUserPermissionsLoading }] =
    useSetUserPermissionsMutation();
  const { data, loading } = useApplicationsPaginatedListQuery({
    variables: {
      limit: appState.limit,
      page: appState.page,
    },
  });

  const onSubmit = async ({ email }: UserPermissions) => {
    const inputApplications =
      userRole === Role.Admin ? [] : selectedApplications;

    try {
      await setUserPermissions({
        variables: {
          email,
          input: {
            role: userRole,
            applications: inputApplications,
          },
        },
      });

      reset();
      onClose();
      refetch();
    } catch (err) {
      handleError(err);
    }

    setSelectedApplications([]);
    !edit && setUserRole(Role.Admin);
  };

  const onAppSelect = (id: string) => {
    if (selectedApplications.includes(id)) {
      const selected = selectedApplications.filter(
        (appId: string) => appId !== id
      );
      setSelectedApplications(selected);
    } else {
      setSelectedApplications([...selectedApplications, id]);
    }
  };

  const onCloseAppSelector = () => {
    closeAppSidebar();
    !edit && setSelectedApplications([]);
  };

  useEffect(() => {
    if (edit) {
      const initialSelectedApplications =
        editItem?.access?.applicationsWithNames
          ?.filter(({ name }) => isNotNil(name))
          ?.map(({ nid }) => nid) ?? [];

      setSelectedApplications(initialSelectedApplications);
      setValue("email", editItem?.email);
      setUserRole(
        editItem?.access?.role === "ADMIN" ? Role.Admin : Role.TeamLead
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editItem, edit]);

  useEffect(() => {
    !edit && setUserRole(Role.Admin);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        title={edit ? "Edit permissions" : "Add new User"}
        footer={
          <>
            <span />
            <Button
              m={theme.spacing.spacing04}
              ml="0"
              type="submit"
              form="addUserForm"
              icon="plus"
            >
              {edit ? "Edit permissions" : "Add new User"}
            </Button>
          </>
        }
        onClick={onClose}
      >
        <LoaderWrap inContent loading={isSetUserPermissionsLoading}>
          <form id="addUserForm" onSubmit={handleSubmit(onSubmit)}>
            <Fieldset>
              <Label required name="Email" />
              <If condition={edit}>
                <Then>
                  <Input disabled={edit} defaultValue={editItem?.email} />
                  <input
                    type="hidden"
                    {...register("email", { required: true })}
                    defaultValue={editItem?.email}
                  />
                </Then>
                <Else>
                  <Input
                    type="text"
                    {...register("email", { required: true })}
                    defaultValue=""
                    placeholder="Email"
                  />
                  {errors.email && (
                    <Description>Email is required.</Description>
                  )}
                </Else>
              </If>
            </Fieldset>
            <Fieldset>
              <Label required name="User Role" />
              <Dropdown
                value={`${userRole}`}
                name="Select User Role"
                options={userOptions}
                title="Select User Role"
                onChange={(newValue) => setUserRole(newValue as Role)}
              />
              {errors.role && <Description>Role is required.</Description>}
            </Fieldset>
            {userRole === Role.TeamLead && (
              <Fieldset>
                <Label name="Permissions" />
                <FlexContainer
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <div>{`${selectedApplications.length} applications selected`}</div>
                  <Tag icon="plus" text="Select" onClick={openAppSidebar} />
                </FlexContainer>
              </Fieldset>
            )}
          </form>
        </LoaderWrap>
      </Sidebar>
      <Sidebar
        isOpen={isAppSidebarOpen}
        title="Select applications"
        footer={
          <>
            <span />
            <Button
              m={theme.spacing.spacing04}
              ml="0"
              icon="plus"
              onClick={closeAppSidebar}
            >
              Apply
            </Button>
          </>
        }
        onClick={onCloseAppSelector}
      >
        <LoaderWrap inContent loading={loading}>
          {data?.applicationsPaginated?.results?.map(
            ({ nid, name, description }: ApplicationData) => (
              <ApplicationItem
                key={nid}
                onClick={() => {
                  onAppSelect(nid);
                }}
              >
                <FlexContainer justifyContent="space-between">
                  <div>
                    <Heading level={4}>{name}</Heading>
                    <Text size="sm" tag="div" color={theme.color.text.text02}>
                      {description || nid}
                    </Text>
                  </div>
                  <Switch
                    checked={selectedApplications.includes(nid)}
                    onClick={(event) => {
                      event.stopPropagation();
                      event.preventDefault();
                    }}
                  />
                </FlexContainer>
              </ApplicationItem>
            )
          )}
          <ExtendedPaginationBox
            small
            count={data?.applicationsPaginated?.count ?? 0}
            from={appState.page * appState.limit}
            size={appState.limit}
            setPage={(newPage) =>
              setAppState({
                ...appState,
                page: Math.ceil(newPage / appState.limit),
              })
            }
            setSize={(limit) => {
              setAppState({ page: 0, limit });
            }}
          />
        </LoaderWrap>
      </Sidebar>
    </>
  );
}

const ApplicationItem = styled.div`
  padding: ${theme.spacing.spacing03} ${theme.spacing.spacing02};
  border-bottom: 1px solid ${theme.color.border.border01};

  &:hover {
    cursor: pointer;
    background-color: ${theme.color.background.ui03};
  }
`;

const userOptions = [
  { value: Role.Admin, label: showRole(Role.Admin) },
  { value: Role.TeamLead, label: showRole(Role.TeamLead) },
];

type ApplicationData = {
  nid: string;
  name: string;
  description?: string | null;
};
